import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getScenario, SCENARIOS } from '@/fixtures/scenarios';
import { formatRangeShort, isValidDate, previousPeriod } from '@/domain/time';
import { machineLabel, resolveMachineId } from '@/lib/present';
import { buildQuery } from '@/metrics/engine';
import { KEYS } from './storage';
import { MACHINES, FRACTIONS } from '@/fixtures/generator';

const SITES = [{ id: 'dimmelsvik', label: 'Dimmelsvik' }];

/**
 * Én query-tilstand styrer alle sider. Delbare filtre i URL; siste visning lagres lokalt.
 * Parametre: scenario, fra, til, sml, anlegg, omfang, maskin, fraksjon.
 */
export function parseFilters(sp) {
  const scenario = getScenario(sp.get('scenario'));
  let fromDate = isValidDate(sp.get('fra')) ? sp.get('fra') : scenario.recommendedFrom;
  let toDate = isValidDate(sp.get('til')) ? sp.get('til') : scenario.recommendedTo;
  if (!sp.get('til') && sp.get('fra')) toDate = fromDate;
  if (toDate < fromDate) toDate = fromDate;
  const sml = sp.get('sml') ?? 'forrige';
  let compare;
  let compareMode = 'forrige';
  const manual = /^(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})$/.exec(sml);
  if (manual && isValidDate(manual[1]) && isValidDate(manual[2])) {
    compare = { fromDate: manual[1], toDate: manual[2] < manual[1] ? manual[1] : manual[2] };
    compareMode = 'manuell';
  } else if (sml === 'scenario') {
    compare = { fromDate: scenario.compareFrom, toDate: scenario.compareTo };
    compareMode = 'scenario';
  } else {
    compare = previousPeriod(fromDate, toDate);
  }
  const scope = sp.get('omfang') === 'alle' ? 'all' : 'production';
  const machineIds = (sp.get('maskin') ?? '')
    .split(',')
    .map((s) => resolveMachineId(s.trim(), MACHINES.map((m) => m.machineId)))
    .filter((s) => MACHINES.some((m) => m.machineId === s));
  const fractionId = FRACTIONS.some((f) => f.fractionId === sp.get('fraksjon')) ? sp.get('fraksjon') : null;
  const siteId = SITES.some((s) => s.id === sp.get('anlegg')) ? sp.get('anlegg') : 'dimmelsvik';
  return { scenario, scenarioId: scenario.id, fromDate, toDate, compare, compareMode, scope, machineIds, fractionId, siteId };
}

export function useFilters() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const f = useMemo(() => parseFilters(sp), [sp]);

  useEffect(() => {
    window.localStorage.setItem(KEYS.lastView, `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  const set = useCallback(
    (changes, { replace = false } = {}) => {
      const next = new URLSearchParams(sp);
      for (const [k, v] of Object.entries(changes)) {
        if (v === null || v === undefined || v === '' || v === false) next.delete(k);
        else next.set(k, Array.isArray(v) ? v.join(',') : String(v));
      }
      setSp(next, { replace });
    },
    [sp, setSp],
  );

  const setPeriod = useCallback((fromDate, toDate) => set({ fra: fromDate, til: toDate ?? fromDate }), [set]);

  const setScenario = useCallback(
    (id) => {
      const sc = getScenario(id);
      const next = new URLSearchParams();
      next.set('scenario', sc.id);
      setSp(next);
      return sc;
    },
    [setSp],
  );

  const reset = useCallback(() => {
    const next = new URLSearchParams();
    if (sp.get('scenario')) next.set('scenario', sp.get('scenario'));
    setSp(next);
  }, [sp, setSp]);

  const query = useMemo(
    () => buildQuery({ fromDate: f.fromDate, toDate: f.toDate, scenarioId: f.scenarioId, scope: f.scope, machineIds: f.machineIds, fractionId: f.fractionId, siteId: f.siteId }),
    [f],
  );
  const refQuery = useMemo(
    () => buildQuery({ fromDate: f.compare.fromDate, toDate: f.compare.toDate, scenarioId: f.scenarioId, scope: f.scope, machineIds: f.machineIds, fractionId: f.fractionId, siteId: f.siteId }),
    [f],
  );

  const chips = useMemo(() => {
    const out = [];
    const sc = f.scenario;
    if (f.fromDate !== sc.recommendedFrom || f.toDate !== sc.recommendedTo) out.push({ key: 'periode', label: `Periode: ${formatRangeShort(f.fromDate, f.toDate)}`, clear: { fra: null, til: null } });
    if (f.compareMode !== 'forrige') out.push({ key: 'sml', label: `Sammenligning: ${formatRangeShort(f.compare.fromDate, f.compare.toDate)}`, clear: { sml: null } });
    if (f.scope === 'all') out.push({ key: 'omfang', label: 'Omfang: alle maskiner', clear: { omfang: null } });
    if (f.machineIds.length) out.push({ key: 'maskin', label: `Maskin: ${f.machineIds.map(machineLabel).join(', ')}`, clear: { maskin: null } });
    if (f.fractionId) out.push({ key: 'fraksjon', label: `Fraksjon: ${FRACTIONS.find((x) => x.fractionId === f.fractionId)?.name ?? f.fractionId}`, clear: { fraksjon: null } });
    return out;
  }, [f]);

  /** Lenke til annen rute med beholdte filtre (og evt. overstyringer). */
  const linkTo = useCallback(
    (path, overrides = {}) => {
      const next = new URLSearchParams(sp);
      for (const [k, v] of Object.entries(overrides)) {
        if (v === null || v === undefined || v === '') next.delete(k);
        else next.set(k, String(v));
      }
      const qs = next.toString();
      return qs ? `${path}?${qs}` : path;
    },
    [sp],
  );

  const go = useCallback((path, overrides = {}) => navigate(linkTo(path, overrides)), [navigate, linkTo]);

  return { ...f, sp, set, setPeriod, setScenario, reset, query, refQuery, chips, linkTo, go, scenarios: SCENARIOS, sites: SITES };
}
